import { useState } from "react";
import LegalLayout from "@/components/LegalLayout";
import { ChevronDown } from "lucide-react";

const FAQS = [
  {
    q: "What is PintaFlow?",
    a: "A Creator OS — a workspace where you capture ideas, plan content, write AI-assisted scripts, track your publishing rhythm, and see what's actually growing your channel. Think of it as your studio, your notebook, and your coach in one place.",
  },
  {
    q: "Is it free?",
    a: "Yes. The Starter plan is free forever with everything a solo creator needs. Paid plans (with higher AI limits, deeper analytics and priority support) will roll out later — beta users will get grandfathered pricing.",
  },
  {
    q: "Do I need to connect my YouTube channel?",
    a: "No. You can use PintaFlow entirely without connecting any social account — just log metrics manually. Connecting YouTube is optional and unlocks auto-sync of views/likes/comments so you don't have to type them in.",
  },
  {
    q: "What data does PintaFlow collect from YouTube?",
    a: "Only public channel stats and video metadata (title, views, likes, comments) via read-only YouTube API. We never post on your behalf, never see your Studio, and never touch other Google services (Gmail, Drive, etc.).",
  },
  {
    q: "How does the AI Co-Pilot work?",
    a: "You give it a topic, tone and platform — it writes a structured script (Hook, Intro, Body, CTA) using GPT-5.2. Free plan includes 5 scripts/month. The output is a starting point, not final copy — always edit before publishing.",
  },
  {
    q: "What is Today Mode?",
    a: "Your homepage. Every day PintaFlow surfaces exactly 3 growth moves based on your weakest areas — with expected result, time estimate, and a direct action button. Complete these 3, and everything else can wait.",
  },
  {
    q: "What is Creator Momentum?",
    a: "A 0-100 score across 5 dimensions — Consistency, Planning, Engagement, Analytics, Networking. It's feedback, not judgment. Weak areas are called Growth Opportunities and drive tomorrow's 3 moves.",
  },
  {
    q: "Can I write my own daily moves?",
    a: "Yes — expand 'Prep tomorrow's 3 moves' at the bottom of /today. Any moves you plan the night before override the auto-generated set.",
  },
  {
    q: "Can I use PintaFlow on my phone?",
    a: "Absolutely. PintaFlow is a Progressive Web App — install it from your browser (Add to Home Screen on iOS, Install on Android). Works offline too, syncs when you're back online.",
  },
  {
    q: "How do I export my data?",
    a: "Settings → Export → CSV. Your ideas, scripts, metrics and swipes all come out in a clean CSV bundle. Your data is always yours.",
  },
  {
    q: "How do I delete my account?",
    a: "Email hello@pintaflow.com from your signup email. We'll confirm within 24 hours and permanently delete your workspace within 7 days.",
  },
  {
    q: "Is there a refund if I don't like the paid plan?",
    a: "Yes — a 7-day no-questions-asked refund on your first paid subscription. See the Refund Policy for details.",
  },
  {
    q: "How is PintaFlow different from Notion / Trello / Airtable?",
    a: "Those are general-purpose tools you have to configure. PintaFlow is built exclusively for creators — the workflows, the analytics, the AI, the momentum score, the streaks — every screen is tuned for one job: help you ship consistent content.",
  },
  {
    q: "Do you have an affiliate / referral programme?",
    a: "Coming soon. Follow @pradeepmunjal on Twitter for the launch — beta users will get first access + higher commission rates.",
  },
  {
    q: "Can I request a feature?",
    a: "Please do. Tap the Feedback button (bottom-right on any signed-in page) or email hello@pintaflow.com. Roughly 30% of features on the roadmap came from user requests.",
  },
];

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      data-testid={`faq-item-${index}`}
      className="rounded-xl overflow-hidden"
      style={{ background: "var(--surface)", border: "1px solid var(--ink-22)" }}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`faq-toggle-${index}`}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="text-[15px]" style={{ color: "var(--ink)", fontVariationSettings: "'wght' 500" }}>
          {q}
        </span>
        <ChevronDown
          size={16}
          strokeWidth={1.8}
          style={{
            color: "var(--seal)",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 200ms ease",
          }}
        />
      </button>
      {open && (
        <div
          data-testid={`faq-answer-${index}`}
          className="px-5 pb-4 text-[14px] leading-relaxed"
          style={{ color: "var(--ink-72)", borderTop: "1px solid var(--ink-22)", paddingTop: "1rem" }}
        >
          {a}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  return (
    <LegalLayout
      eyebrow="vii. Common questions"
      title="Frequently"
      italic="asked."
      lastUpdated="July 17, 2026"
      testid="faq-page"
    >
      <p>
        Fifteen questions we hear most often. Something not covered here? Email{" "}
        <a href="mailto:hello@pintaflow.com" style={{ color: "var(--seal)" }}>hello@pintaflow.com</a> — we&apos;ll add it.
      </p>

      <div className="flex flex-col gap-2.5 mt-8" data-testid="faq-list">
        {FAQS.map((f, i) => (
          <FaqItem key={i} index={i} q={f.q} a={f.a} />
        ))}
      </div>
    </LegalLayout>
  );
}
